import { create } from 'zustand';
import type { Session } from '@supabase/supabase-js';

import { authService } from '@/services/auth-service';
import { resolveAccountSwitch } from '@/services/account-switch-service';
import { db } from '@/services/storage/db';
import { applyTheme } from '@/hooks/useTheme';
import { seedBuiltInTemplates } from '@/services/template-seed-service';
import { sentryService } from '@/services/sentry-service';
import { posthogService } from '@/services/posthog-service';
import { EVENT_SIGNUP, EVENT_LOGIN, EVENT_LOGOUT } from '@/services/observability/events';
import type { User, UserPreferences, UpdateUserPreferencesInput } from '@/types/user';

// M187 (#1110) bootstrap-race fix: the session-establishment sites below fire
// refreshProfileSubscription() un-awaited (status flips to 'authenticated' before
// the server tier resolves — see ADR-019 offline-first: the flip must NOT block).
// sync-store's initSync awaits the SAME refresh for its first-sync gate so isPro()
// reads the fresh entitlement rather than the ADR-032 Dexie default ('starter').
// The in-flight-promise cache below joins concurrent callers to a single fetch
// instead of double-firing; it clears when the refresh settles (success OR failure)
// so a later refresh can start fresh.
let inFlightSubscriptionRefresh: Promise<void> | null = null;

// Lazily resolve the user-profile store to break the circular ES module reference.
// auth-store ← user-profile-store (already imports useAuthStore), so importing
// useUserProfileStore at module-evaluation time would form a cycle.
// Dynamic import at call-time defers resolution until both modules are fully loaded.
export function refreshProfileSubscription(): Promise<void> {
  // Join an in-flight refresh so concurrent callers share one fetch.
  if (inFlightSubscriptionRefresh) return inFlightSubscriptionRefresh;

  const refresh = (async () => {
    const { useUserProfileStore } = await import('@/stores/user-profile-store');
    await useUserProfileStore.getState().refreshSubscription();
  })();
  inFlightSubscriptionRefresh = refresh;

  // Clear the shared cache once this refresh settles (success OR failure) so a
  // later refresh starts fresh. Both handlers are provided (fulfilled + rejected)
  // so this bookkeeping chain never surfaces an unhandled rejection; the ORIGINAL
  // `refresh` promise is returned to callers, who own error handling.
  const clear = () => {
    if (inFlightSubscriptionRefresh === refresh) inFlightSubscriptionRefresh = null;
  };
  refresh.then(clear, clear);

  return refresh;
}

interface AuthState {
  user: User | null;
  preferences: UserPreferences | null;
  session: Session | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';

  initialize: () => void;
  signUp: (email: string, password: string, displayName: string, redirect?: string | null) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithMagicLink: (email: string, redirect?: string | null) => Promise<void>;
  signOut: () => Promise<void>;
  updatePreferences: (prefs: Partial<UpdateUserPreferencesInput>) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  preferences: null,
  session: null,
  status: 'loading',

  initialize: () => {
    // Subscribe to Supabase auth events for the lifetime of the app.
    // INITIAL_SESSION fires immediately with the cached session (offline-safe).
    authService.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        set({ user: null, preferences: null, session: null, status: 'unauthenticated' });
        try {
          posthogService.track(EVENT_LOGOUT);
          posthogService.reset();
          sentryService.setUser(null);
        } catch (obsErr) {
          console.error('[AuthStore] Observability error on SIGNED_OUT:', obsErr);
        }
        return;
      }

      if (event === 'TOKEN_REFRESHED' && session) {
        set((state) => ({ ...state, session }));
        return;
      }

      if (session) {
        // INITIAL_SESSION or SIGNED_IN with a valid session
        try {
          db.setCurrentUserId(session.user.id);
          // ADR-070: detect and resolve an account switch BEFORE any further
          // reads/writes or the 'authenticated' status flip — this gates initSync.
          await resolveAccountSwitch(session.user.id);
          let user = await db.getUserById(session.user.id);
          if (!user) {
            user = await db.createUser({
              id: session.user.id,
              email: session.user.email ?? '',
              displayName: session.user.email ?? '',
              defaultState: '',
              preferredUnits: 'imperial',
            });
          }
          const preferences = await db.getUserPreferences(session.user.id);
          applyTheme(preferences?.theme ?? 'dark');
          set({ user, preferences, session, status: 'authenticated' });
          try {
            sentryService.setUser({ id: session.user.id });
            posthogService.identify(session.user.id);
          } catch (obsErr) {
            console.error('[AuthStore] Observability error on auth session:', obsErr);
          }
          // Hydrate server-authoritative billing tier — non-blocking, best-effort
          refreshProfileSubscription().catch((err) => {
            console.warn('[AuthStore] Subscription refresh failed on session load:', err);
          });
          // Seed built-in templates after auth resolves — non-blocking, best-effort
          seedBuiltInTemplates(db).catch((err) => {
            console.warn('[AuthStore] Template seed failed (will retry on next launch):', err);
          });
        } catch (err) {
          console.error('[AuthStore] Failed to load user data after auth event:', err);
          set({ status: 'unauthenticated' });
        }
      } else if (event === 'INITIAL_SESSION') {
        // INITIAL_SESSION with no session = not logged in
        set({ status: 'unauthenticated' });
      }
    });
  },

  signUp: async (email, password, displayName, redirect) => {
    const { user, emailConfirmationRequired } = await authService.signUp(
      email,
      password,
      displayName,
      redirect ?? null,
    );

    if (emailConfirmationRequired) {
      // Supabase project requires email confirmation. No session is available yet —
      // skip all session-dependent work (sync, observability, template seeding).
      // The user must click the confirmation link, which redirects to /auth/callback
      // where onAuthStateChange SIGNED_IN will complete the setup.
      // Throw with a sentinel so AuthPage can display a success message.
      throw new Error('EMAIL_CONFIRMATION_REQUIRED');
    }

    db.setCurrentUserId(user.id);
    // ADR-070: resolve an account switch before the 'authenticated' status
    // flip. No new try/catch — a thrown reset propagates to the caller
    // (AuthPage) as the existing failed-signup contract.
    await resolveAccountSwitch(user.id);
    const preferences = await db.getUserPreferences(user.id);
    const session = await authService.getCurrentSession();
    applyTheme(preferences?.theme ?? 'dark');
    set({ user, preferences, session, status: 'authenticated' });
    try {
      sentryService.setUser({ id: user.id });
      posthogService.identify(user.id);
      posthogService.track(EVENT_SIGNUP, { method: 'password' });
    } catch (obsErr) {
      console.error('[AuthStore] Observability error on signUp:', obsErr);
    }
    // Hydrate server-authoritative billing tier — non-blocking, best-effort
    refreshProfileSubscription().catch((err) => {
      console.warn('[AuthStore] Subscription refresh failed after signUp:', err);
    });
    seedBuiltInTemplates(db).catch((err) => {
      console.warn('[AuthStore] Template seed failed after signUp:', err);
    });
  },

  signIn: async (email, password) => {
    const user = await authService.signIn(email, password);
    db.setCurrentUserId(user.id);
    // ADR-070: resolve an account switch before the 'authenticated' status
    // flip. No new try/catch — a thrown reset propagates to the caller
    // (AuthPage) as the existing failed-login contract.
    await resolveAccountSwitch(user.id);
    const preferences = await db.getUserPreferences(user.id);
    const session = await authService.getCurrentSession();
    applyTheme(preferences?.theme ?? 'dark');
    set({ user, preferences, session, status: 'authenticated' });
    try {
      sentryService.setUser({ id: user.id });
      posthogService.identify(user.id);
      posthogService.track(EVENT_LOGIN, { method: 'password' });
    } catch (obsErr) {
      console.error('[AuthStore] Observability error on signIn:', obsErr);
    }
    // Hydrate server-authoritative billing tier — non-blocking, best-effort
    refreshProfileSubscription().catch((err) => {
      console.warn('[AuthStore] Subscription refresh failed after signIn:', err);
    });
    seedBuiltInTemplates(db).catch((err) => {
      console.warn('[AuthStore] Template seed failed after signIn:', err);
    });
  },

  signInWithMagicLink: async (email, redirect) => {
    await authService.signInWithMagicLink(email, redirect ?? null);
    // Auth state will resolve via onAuthStateChange when the user clicks the link
  },

  signOut: async () => {
    await authService.signOut();
    // onAuthStateChange SIGNED_OUT event will clear the store state
  },

  updatePreferences: async (prefs) => {
    const { user } = get();
    if (!user) throw new Error('Not authenticated');
    const preferences = await db.updateUserPreferences(user.id, prefs);
    if (prefs.theme) applyTheme(prefs.theme);
    set({ preferences });
  },
}));
